import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CATEGORY_BY_SLUG, FLEET_SCALE } from "../lib/partnerContent";
import { PartnerEnquiryForm } from "../components/partner/PartnerEnquiryForm";
import { useSeo, breadcrumb } from "../lib/seo";
import { ShieldCheck, ChevronLeft } from "lucide-react";

const SAFEGUARDS = [
  "No commercial terms, trade rates or commissions are published — everything is agreed privately per partnership.",
  "No API or technical build is promised upfront; integration is scoped only where it adds proven value.",
  "Your customer data is used solely to operate the agreed programme — nothing more.",
];

export default function PartnerCategory() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const c = CATEGORY_BY_SLUG[slug];
  useSeo({
    title: c ? `${c.label} Partnerships | Partner With Europcar UAE` : "Partner With Europcar",
    description: c ? c.sub : "",
    jsonLd: c ? [breadcrumb([["Home", "/"], ["Partner With Us", "/partner-with-us"], [c.label, `/partner-with-us/${slug}`]])] : [],
  });
  if (!c) return <Navigate to="/partner-with-us" replace />;

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="relative bg-[#0F1E17]">
        <img src={c.image} alt={`${c.label} partnership with Europcar`} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <Link to="/partner-with-us" className="text-white/70 text-xs flex items-center gap-1 mb-4 hover:text-white" data-testid="partner-back-link"><ChevronLeft className="w-4 h-4" /> {t("All partnerships")}</Link>
          <div className="promo-stripe max-w-2xl">
            <span className="text-[10px] uppercase tracking-widest text-[#FFDC00] font-bold">{t(c.label)}</span>
            <h1 className="heading-upper text-2xl sm:text-3xl text-white mt-1" data-testid="partner-category-heading">{t(c.headline)}</h1>
            <p className="text-white/85 text-sm mt-3 leading-relaxed">{t(c.sub)}</p>
            <p className="text-white/60 text-[11px] mt-3">{t(FLEET_SCALE)}</p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 w-full flex-1">
        <div className="grid lg:grid-cols-[1fr_420px] gap-10 items-start">
          <div className="space-y-10">
            <section data-testid="partner-models">
              <div className="promo-stripe mb-4"><h2 className="heading-upper text-lg text-[#144733]">{t("Partnership models")}</h2></div>
              <div className="grid sm:grid-cols-3 gap-4">
                {c.models.map((m) => (
                  <div key={m.name} className="bg-white border border-[#E2E8F0] rounded-2xl p-4">
                    <h3 className="font-bold text-xs text-[#0F1E17]">{t(m.name)}</h3>
                    <p className="text-[11px] text-[#57685e] mt-1.5 leading-relaxed">{t(m.text)}</p>
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="partner-journey">
              <div className="promo-stripe mb-4"><h2 className="heading-upper text-lg text-[#144733]">{t("The journey")}</h2></div>
              <div className="bg-white border border-[#e2ebe4] rounded-2xl overflow-hidden">
                {c.journey.map(([step, detail], i) => (
                  <div key={step} className="grid sm:grid-cols-[40px_1fr_1fr] gap-2 sm:gap-4 p-3 border-b border-[#f1f3f1] last:border-0 items-center">
                    <span className="w-7 h-7 rounded-full bg-[#144733] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm font-semibold text-[#0F1E17]">{t(step)}</span>
                    <span className="text-xs text-[#57685e]">{t(detail)}</span>
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="partner-values">
              <div className="promo-stripe mb-4"><h2 className="heading-upper text-lg text-[#144733]">{t("What you get")}</h2></div>
              <ul className="space-y-2">
                {c.values.map((v) => (
                  <li key={v} className="flex gap-2 text-sm text-[#41504a]"><span className="text-[#009900] font-bold">·</span>{t(v)}</li>
                ))}
              </ul>
            </section>

            <section className="bg-[#fafcfa] border border-dashed border-[#d5ddd5] rounded-2xl p-5" data-testid="partner-safeguards">
              <h2 className="heading-upper text-sm tracking-widest text-[#144733] flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-[#009900]" /> {t("Our commitments")}</h2>
              <ul className="space-y-2">
                {SAFEGUARDS.map((s) => <li key={s} className="text-xs text-[#57685e] leading-relaxed">— {t(s)}</li>)}
              </ul>
              <p className="text-xs text-[#41504a] mt-4 leading-relaxed"><strong>{t("For your customers:")}</strong> {t(c.endCustomer)}</p>
            </section>
          </div>

          <div className="lg:sticky lg:top-24" data-testid="partner-form-panel">
            <div className="promo-stripe mb-4"><h2 className="heading-upper text-lg text-[#144733]">{t("Start the conversation")}</h2></div>
            <PartnerEnquiryForm category={c} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
